/**
 * 项目相关API服务
 */
import api from './api';
import {
  Project,
  ProjectStatus,
  ApiResponse,
  PaginatedResponse,
  BudgetBreakdown,
  BudgetMode,
  RevenueBreakdown,
} from '../types';

// 创建项目的数据类型
export interface CreateProjectData {
  name: string;
  projectCode?: string;
  description?: string;
  startDate: string;
  endDate?: string;
  budget?: number;
  budgetBreakdown?: BudgetBreakdown;
  revenueTotal?: number;
  revenueBreakdown?: RevenueBreakdown;
  budgetVersionReason?: string;
  budgetMode?: BudgetMode;
  progress?: number;
}

// 更新项目的数据类型
export interface UpdateProjectData {
  name?: string;
  projectCode?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  status?: ProjectStatus;
  budget?: number;
  budgetBreakdown?: BudgetBreakdown;
  revenueTotal?: number;
  revenueBreakdown?: RevenueBreakdown;
  budgetVersionReason?: string;
  budgetMode?: BudgetMode;
  progress?: number;
}

// 项目统计数据
export interface ProjectStats {
  total: number;
  active: number;
  completed: number;
  paused: number;
}

export interface ProjectSuggestion {
  _id: string;
  name: string;
  projectCode?: string;
  status: ProjectStatus;
}

type ProjectOption = Pick<Project, '_id' | 'name' | 'status'> & {
  projectCode?: string;
};

export class ProjectService {
  /**
   * 获取项目列表
   */
  static async getProjects(params: {
    status?: ProjectStatus;
    page?: number;
    limit?: number;
    search?: string;
  } = {}): Promise<PaginatedResponse<Project>> {
    return api.get('/projects', { params }) as Promise<PaginatedResponse<Project>>;
  }

  /**
   * 根据ID获取项目详情
   */
  static async getProjectById(id: string): Promise<ApiResponse<Project>> {
    return api.get(`/projects/${id}`) as Promise<ApiResponse<Project>>;
  }

  /**
   * 创建新项目
   */
  static async createProject(data: CreateProjectData): Promise<ApiResponse<Project>> {
    return api.post('/projects', data) as Promise<ApiResponse<Project>>;
  }

  /**
   * 更新项目
   */
  static async updateProject(id: string, data: UpdateProjectData): Promise<ApiResponse<Project>> {
    return api.put(`/projects/${id}`, data) as Promise<ApiResponse<Project>>;
  }

  /**
   * 删除项目
   */
  static async deleteProject(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/projects/${id}`) as Promise<ApiResponse<void>>;
  }

  /**
   * 获取所有活跃项目（用于下拉选择）
   */
  static async getActiveProjects(): Promise<ApiResponse<Project[]>> {
    const pageSize = 100;
    const getPage = (page: number) => api.get('/projects/options/active', {
      params: { page, limit: pageSize },
    }) as Promise<any>;

    const firstPage = await getPage(1);
    const totalPages = Math.max(1, Number(firstPage?.pagination?.totalPages) || 1);
    const remainingPages = totalPages > 1
      ? await Promise.all(Array.from({ length: totalPages - 1 }, (_, index) => getPage(index + 2)))
      : [];

    const projects = [firstPage, ...remainingPages]
      .flatMap((payload) => Array.isArray(payload?.data) ? payload.data : []);
    const uniqueProjects = Array.from(
      new Map(projects.map((project: ProjectOption) => [project._id, project])).values()
    ).sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'));

    return {
      success: firstPage?.success ?? true,
      data: uniqueProjects as Project[],
      message: firstPage?.message || '',
    };
  }

  /**
   * 获取项目统计信息
   */
  static async getProjectStats(): Promise<ApiResponse<ProjectStats>> {
    return api.get('/projects/stats/overview') as Promise<ApiResponse<ProjectStats>>;
  }

  /**
   * 更新项目状态
   */
  static async updateProjectStatus(id: string, status: ProjectStatus): Promise<ApiResponse<Project>> {
    return this.updateProject(id, { status });
  }

  /**
   * 获取项目搜索建议
   */
  static async getProjectSuggestions(keyword: string, limit = 10): Promise<ApiResponse<ProjectSuggestion[]>> {
    return api.get('/projects/suggestions', {
      params: { keyword, limit }
    }) as Promise<ApiResponse<ProjectSuggestion[]>>;
  }
}
